import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import axios from 'axios';
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  List,
  ListItem,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Check as CheckIcon, Autorenew as AutorenewIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  CustomIconButton,
  DefaultContainedButton,
  DefaultOutlinedButton,
  RedOutlinedButton,
} from '../../../../utils/Buttons';
import { CommonLabel } from '../../../../utils/Common';
import { DecisionDialog } from '../../../../utils/DecisionDialog';
import { EntitiesDialog } from '../../../../utils/EntitiesDialog';
import { ErrorUI, HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { LoadingUI } from '../../../../utils/LayoutStyles';
import { CustomAutocomplete, CustomDatePicker, CustomTextField } from '../../../../utils/StyledInputs';
import GdMasterEntitySelect from '../../../../components/registration/GdMasterEntitySelect';
import { isGdEligibleAge } from '../../../../components/registration/registrationConfig';
import API from '../../../../api';
import GenderList from '../../../../model/gender';
import { validatePhoneNumber } from '../../../../utils/numberValidity';
import { useUser } from '../context/UserContext';
import { POPUP_DURATION_DELAY, usePopup } from '../../../../context/PopupContext';

export default function UserDataTab({ userID, canManageUser, canManageUserRoles, canDeleteUsers }) {
  const {
    isLoading,
    errorMessage,
    name,
    surname,
    aliasName,
    gender,
    birthDate,
    email,
    verifiedUser,
    onlineUser,
    lockedUser,
    userEntity,
    userGdEntity,
    gdEntityList,
    getUserInfo,
    setName,
    setSurname,
    setGender,
    setBirthDate,
    fiscalCode,
    birthPlace,
    phoneNumber,
    callCode,
    userRoles,
    useAlias,
    setUserEntity,
    setUserGdEntity,
    setLockedUser,
    setVerifiedUser,
    setOnlineUser,
    setUseAlias,
    setAliasName,
    setFiscalCode,
    setBirthPlace,
    setEmail,
    setCallCode,
    setPhoneNumber,
    setUserRoles,
    userInfo,
    setErrorMessage,
    setUserInfo,
    canModifyUser,
    setDomIsRes,
    domIsRes,
    resCity,
    resCityName,
    resProvince,
    resRegionName,
    resCountry,
    domCity,
    domCityName,
    domProvince,
    domRegionName,
    domCountry,
    resStreet,
    resZipCode,
    domStreet,
    domZipCode,
    allowNews,
    setResProvince,
    setResRegion,
    setResCity,
    setDomProvince,
    setDomRegion,
    setDomCity,
    resRegion,
    domRegion,
    setResCountry,
    setResRegionName,
    setResCityName,
    setResStreet,
    setResZipCode,
    setDomCountry,
    setDomRegionName,
    setDomStreet,
    setDomZipCode,
    setDomCityName,
    isContactUser,
  } = useUser();

  const { showPopup, setPopUpText } = usePopup();

  useEffect(() => {
    getUserInfo();
  }, []);

  const is600Screen = useMediaQuery('(min-width: 600px)');
  const navigate = useNavigate();

  const [errors, setErrors] = useState({
    name: false,
    surname: false,
    fiscal_code: false,
    email: false,
    birth_date: false,
    place_of_birth: false,
    gender: false,
    name_alias: false,
    phone: false,
    call_code: false,
    home_country_id: false,
    home_street: false,
    home_city_id: false,
    home_zip_code: false,
    home_city_name: false,
    home_province_id: false,
    home_region_name: false,
    res_country_id: false,
    res_street: false,
    res_zip_code: false,
    res_city_id: false,
    res_city_name: false,
    res_province_id: false,
    res_region_name: false,
    entity_id: false,
    user_roles: false,
  });

  const [isEditApisRunning, setIsEditApisRunning] = useState(false);

  // user static data
  const [resRegionList, setResRegionList] = useState([]);
  const [resCountriesList, setResCountriesList] = useState([]);
  const [resProvincesList, setResProvincesList] = useState([]);
  const [resCitiesList, setResCitiesList] = useState([]);
  const [domRegionList, setDomRegionList] = useState([]);
  const [domCountriesList, setDomCountriesList] = useState([]);
  const [domProvincesList, setDomProvincesList] = useState([]);
  const [domCitiesList, setDomCitiesList] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [birthPlacesList, setBirthPlacesList] = useState([]);
  const [EntitiesList, setEntitiesList] = useState([]);
  const [italy, setItaly] = useState({});

  const getGenderDBName = (name) => {
    let gender;

    if (name === 'uomo') {
      gender = 'male';
    } else if (name === 'donna') {
      gender = 'female';
    } else if (name === 'altro') {
      gender = 'other';
    } else {
      gender = null;
    }

    return gender;
  };

  const getRolesList = () => {
    if (rolesList.length === 0) {
      API.get('/roles')
        .then((response) => {
          setRolesList(response.data.roles);
        })
        .catch((error) => {
          HttpErrorHandler(error, setErrorMessage);
        });
    }
  };

  const getBirthPlacesList = () => {
    if (birthPlacesList.length === 0) {
      API.get('/citiesfiscalcode')
        .then((response) => {
          setBirthPlacesList(response.data);
        })
        .catch((error) => {
          HttpErrorHandler(error, setErrorMessage);
        });
    }
  };

  /* ---------------------------- Update User Data ---------------------------- */
  const [edit, setEdit] = useState(false);

  // Change Birth Date
  const handleBirthDateChange = (date) => {
    const formattedDate = dayjs(date).format('MM/DD/YYYY');
    setBirthDate(formattedDate);
  };

  // Change Dom Is Res
  const handleSwitchChange = () => {
    setDomIsRes(!domIsRes);
  };

  // close edit user data
  const handleCloseEditButtonClick = () => {
    getUserInfo();
    setEdit(!edit);
  };

  // submit update user data
  const updateUserData = (e) => {
    e.preventDefault();
    getRolesList();
    getBirthPlacesList();
    setEdit(true);

    if (edit) {
      // Res Ids

      const CityID = resCity === null ? null : resCity.id ? resCity.id : resCity === '' ? userInfo.res_city_id : null;

      const _resCityName = typeof resCityName === 'string' ? resCityName : null;

      const ProvinceID =
        resProvince === null
          ? null
          : resProvince.id
          ? resProvince.id
          : resProvince === ''
          ? userInfo.res_province_id
          : null;

      const _resRegionName = typeof resRegionName === 'string' ? resRegionName : null;

      const CountryID =
        resCountry === null ? null : resCountry.id ? resCountry.id : resCountry === '' ? userInfo.res_country_id : null;

      // Dom Ids
      const domCityID = domIsRes
        ? CityID
        : !domIsRes && typeof domCity.id === 'string' && domCity.id !== ''
        ? domCity.id
        : !domIsRes && typeof domCity.id === 'string' && domCity.id === ''
        ? null
        : null;

      const _domCityName = typeof domCityName === 'string' ? domCityName : null;

      const domProvinceID = domIsRes
        ? ProvinceID
        : !domIsRes && domProvince && typeof domProvince.id === 'string' && domProvince.id !== ''
        ? domProvince.id
        : !domIsRes && domProvince && typeof domProvince.id === 'string' && domProvince.id === ''
        ? null
        : null;

      const _domRegionName = typeof domRegionName === 'string' ? domRegionName : null;

      const domCountryID = domIsRes
        ? CountryID
        : !domIsRes && domCountry && typeof domCountry.id === 'string' && domCountry.id !== ''
        ? domCountry.id
        : !domIsRes && domCountry && typeof domCountry.id === 'string' && domCountry.id === ''
        ? null
        : null;

      const domStreetValue = domIsRes ? resStreet : domStreet;
      const domZipCodeValue = domIsRes ? resZipCode : domZipCode;

      const formattedData = birthDate ? dayjs(birthDate).format('YYYY/MM/DD') : null;

      const genderName = getGenderDBName(gender && gender.gender ? gender.gender.toLowerCase() : '');

      const updatedData = {
        name,
        surname,
        fiscal_code: fiscalCode?.toUpperCase(),
        email: email && email.length > 0 ? email.replace(/\s+/g, '') : '',
        name_alias: aliasName,
        gender: genderName,
        birth_date: formattedData,
        place_of_birth: birthPlace?.id,
        phone: phoneNumber,
        call_code: callCode || '',
        res_equal_home: Number(domIsRes),
        res_country_id: CountryID,
        res_street: resStreet,
        res_zip_code: resZipCode,
        home_country_id: domCountryID,
        home_street: domStreetValue,
        home_zip_code: domZipCodeValue,
        user_online: userInfo.user_online,
        entity_id: userEntity ? userEntity.id : '',
        entity_master_gd: userGdEntity ? userGdEntity.id : null,
        allow_news: Number(allowNews),
        created_at: userInfo.created_at,
        updated_at: userInfo.updated_at,
        user_roles: userRoles,
        pipeline_stage: userInfo.pipeline_stage,
      };

      let itemsLeft = false;
      const newErrors = { ...errors };

      const requiredFields = isContactUser ? ['entity_id', 'email'] : null;

      if (isContactUser && phoneNumber && phoneNumber.trim().length > 0) {
        requiredFields.push('call_code');
      }

      Object.keys(updatedData).forEach((key) => {
        const value = updatedData[key];

        //  SE pipeline_stage === 'contact'
        if (isContactUser) {
          if (requiredFields.includes(key)) {
            if (value === undefined || value === null || (typeof value === 'string' && value.trim().length === 0)) {
              itemsLeft = true;
              newErrors[key] = true;
              showPopup('warning', 'Inserisci i dati mancanti');
            } else {
              newErrors[key] = false;
            }
          } else {
            // tutti gli altri campi NON sono obbligatori
            newErrors[key] = false;
          }

          return;
        }

        // VALIDAZIONE ORIGINALE (per member / altri stage)
        if (
          value === undefined ||
          value === null ||
          value === 'Invalid Date' ||
          (typeof value === 'string' && value.length === 0) ||
          (Array.isArray(value) && value.length === 0)
        ) {
          if (
            (updatedData.user_online || !['phone', 'call_code', 'email'].includes(key)) &&
            (useAlias || !['name_alias'].includes(key)) &&
            // entity_master_gd è facoltativo (nullable lato BE): per gli utenti non eleggibili ai GD
            // (es. over 30) è null e non deve bloccare il salvataggio del profilo.
            key !== 'entity_master_gd'
          ) {
            itemsLeft = true;
            if (Object.prototype.hasOwnProperty.call(newErrors, key)) {
              newErrors[key] = true;
            }
            showPopup('warning', 'Inserisci i dati mancanti');
          }
        } else {
          newErrors[key] = false;
        }
      });

      updatedData.res_city_id = typeof _resCityName !== 'string' ? CityID : null;
      updatedData.res_city_name = _resCityName;
      updatedData.res_province_id = typeof _resRegionName !== 'string' ? ProvinceID : null;
      updatedData.res_region_name = _resRegionName;
      updatedData.home_city_id = domIsRes ? updatedData.res_city_id : domCityID;
      updatedData.home_city_name = domIsRes ? _resCityName : _domCityName;
      updatedData.home_province_id = domIsRes ? updatedData.res_province_id : domProvinceID;
      updatedData.home_region_name = domIsRes ? _resRegionName : _domRegionName;
      setErrors(newErrors);

      if (!itemsLeft) {
        API.put(`/users/${userInfo.id}`, updatedData)
          .then((response) => {
            setUserInfo((prevUserInfo) => ({
              ...prevUserInfo,
              member: response.data.user.member,
              verified: response.data.user.verified,
            }));
            setPopUpText('Informazioni salvate');
            showPopup(true);

            getUserInfo();
            setEdit(false);

            const userData = JSON.parse(localStorage.getItem('userData'));

            if (userInfo.id === userData.id) {
              // Unione con i dati esistenti: sostituirli farebbe perdere i flag di permesso
              // (can_import, can_manage_newsletters, …) e le relative voci di menù fino al login.
              const newUserData = {
                ...userData,
                birth_date: updatedData.birth_date,
                id: userInfo.id,
                allow_news: updatedData.allow_news,
                name: updatedData.name,
                roles: userInfo.profiles,
                surname: updatedData.surname,
                name_alias: updatedData.name_alias,
                fiscal_code: updatedData.fiscal_code,
                verified: updatedData.verified,
                card_holder: updatedData.card_holder,
              };

              localStorage.removeItem('userData');
              localStorage.setItem('userData', JSON.stringify(newUserData));
            }
          })
          .catch((error) => {
            HttpErrorHandler(error, setPopUpText);
            showPopup(false);
          });
      }
    } else {
      callEditApis();
    }
  };

  /* ----------------------------------- OTP ---------------------------------- */
  const [emailMenuAnchor, setEmailMenuAnchor] = useState(null);
  const [phoneMenuAnchor, setPhoneMenuAnchor] = useState(null);

  const openEmailMenu = Boolean(emailMenuAnchor);
  const openPhoneMenu = Boolean(phoneMenuAnchor);

  const handleClickPhoneMenu = (event) => {
    setPhoneMenuAnchor(event.currentTarget);
  };

  const handleClosePhoneMenu = () => {
    setPhoneMenuAnchor(null);
  };

  const [openVerifyPhoneDialog, setOpenVerifyPhoneDialog] = useState(false);
  const [verifiedPhone, setVerifiedPhone] = useState(false);
  const [isUserVerifyingPhone, setIsUserVerifyingPhone] = useState(false);

  // Open Verify Phone Dialog
  const handleOpenVerifyPhoneDialog = () => {
    setOpenVerifyPhoneDialog(true);
    handleClosePhoneMenu();
  };

  // Close Verify Phone Dialog
  const handleCloseVerifyPhoneDialog = () => {
    setOpenVerifyPhoneDialog(false);
    setIsUserVerifyingPhone(false);
  };

  const sendOtpSms = () => {
    setIsUserVerifyingPhone(true);

    const countryCode = callCode.slice(1, callCode.length);
    API.patch(`/otp/send_sms?phone=${phoneNumber}&country_code=${countryCode}`)
      .then((response) => {
        setPopUpText('SMS inviato con successo');
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const verifyUserPhone = () => {
    handleClosePhoneMenu();
    const data = {
      verified_phone: 1,
    };

    API.patch(`/users/${userInfo.id}/verify_phone`, data)
      .then((response) => {
        setVerifiedPhone(true);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const handleClickEmailMenu = (event) => {
    setEmailMenuAnchor(event.currentTarget);
  };

  const handleCloseEmailMenu = () => {
    setEmailMenuAnchor(null);
  };

  const [openVerifyEmailDialog, setOpenVerifyEmailDialog] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState(false);
  const [isUserVerifyingEmail, setIsUserVerifyingEmail] = useState(false);

  // Open Verify Email Dialog
  const handleOpenVerifyEmailDialog = () => {
    setOpenVerifyEmailDialog(true);
    handleCloseEmailMenu();
  };

  // Close Verify Email Dialog
  const handleCloseVerifyEmailDialog = () => {
    setOpenVerifyEmailDialog(false);
    setIsUserVerifyingEmail(false);
  };

  const sendOtpEmail = () => {
    setIsUserVerifyingEmail(true);

    API.patch(`/otp/send_mail`, { email: `${email}` })
      .then((response) => {
        setPopUpText('Email inviata con successo');
        showPopup(true);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const verifyUserEmail = () => {
    handleCloseEmailMenu();
    const data = {
      verified_email: 1,
    };

    API.patch(`/users/${userInfo.id}/verify_email`, data)
      .then((response) => {
        setVerifiedEmail(true);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // Get api calls for countries, regions, provinces, cities and entities
  const callEditApis = async () => {
    if (resCountriesList.length === 0 || domCountriesList.length === 0 || EntitiesList.length === 0) {
      setIsEditApisRunning(true);
      try {
        const responses = await axios.all([API.get('/countries')]);

        const [countriesResponse] = responses;

        setResCountriesList(countriesResponse.data);
        setDomCountriesList(countriesResponse.data);

        const italyData = countriesResponse.data.find((element) => element.name === 'Italia');
        setItaly(italyData);

        setIsEditApisRunning(false);
      } catch (error) {
        setIsEditApisRunning(false);
      }
    }
  };

  useEffect(() => {
    if (edit && resCountry && resCountry?.name !== italy?.name) {
      setResProvince('');
      setResRegion('');
      setResCity('');
    }

    if (resCountry?.name !== italy?.name) {
      setResRegionList([]);
      setResProvincesList([]);
      setResCitiesList([]);
    }
  }, [resCountry]);

  useEffect(() => {
    if (edit && domCountry && domCountry?.name !== italy?.name) {
      setDomProvince('');
      setDomRegion('');
      setDomCity('');
    }

    if (domCountry?.name !== italy?.name) {
      setDomRegionList([]);
      setDomProvincesList([]);
      setDomCitiesList([]);
    }
  }, [domCountry]);

  const [openEntitiesFilter, setOpenEntitiesFilter] = useState(false);

  const handleCloseEntitiesFilterDialog = () => {
    setOpenEntitiesFilter(false);
  };

  const [selectedEntity, setSelectedEntity] = useState(null);

  const getSelectedEntity = (selectedEntity) => {
    setSelectedEntity(selectedEntity);
  };

  useEffect(() => {
    if (selectedEntity) {
      setUserEntity(selectedEntity);
    }
  }, [selectedEntity]);

  // Get resRegionList
  useEffect(() => {
    if (edit) {
      if (resCountry !== null && resCountry !== undefined && resCountry !== '') {
        if (typeof resCountry === 'object' && Object.keys(resCountry).length > 0 && resCountry?.name === italy.name) {
          API.get(`/countries/regions`, {
            params: {
              id: resCountry.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResRegionList(response.data);

              if (domIsRes) {
                setDomRegionList(response.data);
              }
            } else {
              setResRegionList([]);
              setResProvincesList([]);
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resCountry, edit, italy]);

  // Get resProvincesList
  useEffect(() => {
    if (edit) {
      if (resRegion !== null && resRegion !== undefined && resRegion !== '') {
        if (typeof resRegion === 'object' && Object.keys(resRegion).length > 0 && resCountry?.name === italy.name) {
          API.get(`/regions/provinces`, {
            params: {
              region_name: resRegion.region_name,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResProvincesList(response.data);

              if (domIsRes) {
                setDomProvincesList(response.data);
              }
            } else {
              setResProvincesList([]);
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resRegion, edit]);

  // Get resCitiesList
  useEffect(() => {
    if (edit) {
      if (resProvince !== null && resProvince !== undefined && resProvince !== '') {
        if (typeof resProvince === 'object' && Object.keys(resProvince).length > 0) {
          API.get(`/provinces/cities`, {
            params: {
              id: resProvince.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setResCitiesList(response.data);
              if (domIsRes) {
                setDomCitiesList(response.data);
              }
            } else {
              setResCitiesList([]);
            }
          });
        }
      }
    }
  }, [resProvince, edit]);

  // Get domRegionList
  useEffect(() => {
    if (edit && !domIsRes) {
      if (domCountry !== null && domCountry !== undefined && domCountry !== '') {
        if (typeof domCountry === 'object' && Object.keys(domCountry).length > 0 && domCountry?.name === italy.name) {
          API.get(`/countries/regions`, {
            params: {
              id: domCountry.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setDomRegionList(response.data);
            } else {
              setDomRegionList([]);
              setDomProvincesList([]);
              setDomCitiesList([]);
            }
          });
        }
      }
    }
  }, [domCountry, edit]);

  // Get domProvincesList
  useEffect(() => {
    if (edit && !domIsRes) {
      if (domRegion !== null && domRegion !== undefined && domRegion !== '') {
        if (typeof domRegion === 'object' && Object.keys(domRegion).length > 0 && domCountry?.name === italy.name) {
          API.get(`/regions/provinces`, {
            params: {
              region_name: domRegion.region_name,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setDomProvincesList(response.data);
            } else {
              setDomProvincesList([]);
              setDomCitiesList([]);
            }
          });
        }
      }
    }
  }, [domRegion, edit]);

  // Get domCitiesList
  useEffect(() => {
    if (edit && !domIsRes) {
      if (domProvince !== null && domProvince !== undefined && domProvince !== '') {
        if (typeof domProvince === 'object' && Object.keys(domProvince).length > 0) {
          API.get(`/provinces/cities`, {
            params: {
              id: domProvince.id,
            },
          }).then((response) => {
            if (response.data.length > 0) {
              setDomCitiesList(response.data);
            } else {
              setDomCitiesList([]);
            }
          });
        }
      }
    }
  }, [domProvince, edit]);

  // Filter Entities List
  useEffect(() => {
    const filteredEntities = [];

    EntitiesList.map((entity) => {
      if (entity.level === 3 && entity.branch === 'NAZ') {
        filteredEntities.push(entity);
      }
      return null;
    });
  }, [EntitiesList]);

  // newsletter subscription dialog
  const [openNewsletterDialog, setOpenNewsletterDialog] = useState(false);

  // open newsletter subscription dialog
  const onOpenNewsletterDialog = () => {
    setOpenNewsletterDialog(true);
  };

  // close newsletter subscription dialog
  const onCloseNewsletterDialog = () => {
    setOpenNewsletterDialog(false);
  };

  // change newsletter permissions
  const changeNewsletterPermission = () => {
    API.patch(`users/${userID}/newsletters`, { allow_news: Number(!allowNews) })
      .then(() => {
        onCloseNewsletterDialog();
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // lock user dialog
  const [openLockedUserDialog, setOpenLockedUserDialog] = useState(false);
  const [blockReasonsList, setBlockReasonsList] = useState([]);
  const [selectedBlockReason, setSelectedBlockReason] = useState(null);

  const getBlockReasons = () => {
    API.get('/blockreasons')
      .then((response) => {
        setBlockReasonsList(response.data);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // open lock user dialog
  const onOpenLockedUserDialog = () => {
    setOpenLockedUserDialog(true);
    if (!lockedUser) {
      getBlockReasons();
    }
  };

  // close lock user dialog
  const onCloseLockedUserDialog = () => {
    setOpenLockedUserDialog(false);
    setSelectedBlockReason(null);
  };

  // lock user
  const handleLockUser = () => {
    API.patch(`admin/users/${userID}/block`, { block_reason_id: selectedBlockReason?.id })
      .then(() => {
        onCloseLockedUserDialog();
        setLockedUser(!lockedUser);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // unlock user
  const handleUnlockUser = () => {
    API.patch(`admin/users/${userID}/unblock`)
      .then(() => {
        onCloseLockedUserDialog();
        setLockedUser(!lockedUser);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  // verify user
  const [openVerifyUserDialog, setOpenVerifyUserDialog] = useState(false);

  // open verify user dialog
  const onOpenVerifyUserDialog = () => {
    setOpenVerifyUserDialog(true);
  };

  // close verify user dialog
  const onCloseVerifyUserDialog = () => {
    setOpenVerifyUserDialog(false);
  };

  // change verified user value
  const changeVerifiedUserValue = () => {
    API.patch(`/users/${userID}/verified?val=${verifiedUser ? 0 : 1}`)
      .then((response) => {
        onCloseVerifyUserDialog();
        setVerifiedUser(!verifiedUser);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  const [openOnlineUserDialog, setOpenOnlineUserDialog] = useState(false);

  const onOpenOnlineUserDialog = () => {
    setOpenOnlineUserDialog(true);
  };

  const onCloseOnlineUserDialog = () => {
    setOpenOnlineUserDialog(false);
  };

  const changeOnlineUserValue = () => {
    API.patch(`/users/${userID}/online?online=${onlineUser ? 0 : 1}`)
      .then((response) => {
        onCloseOnlineUserDialog();
        setOnlineUser(!onlineUser);
        getUserInfo();
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ----------------------------- Reset Password ----------------------------- */
  const [openResetPassword, setOpenResetPassword] = useState(false);

  // Open Reset Password Dialog
  const handleOpenResetPassword = () => {
    setOpenResetPassword(true);
  };

  // Close Reset Password Dialog
  const handleCloseResetPassword = () => {
    setOpenResetPassword(false);
  };

  // submit reset password
  const handleResetPassword = (e) => {
    e.preventDefault();

    const userFiscalCode = {
      fiscal_code: userInfo.fiscal_code,
    };

    API.patch(`/passwordforgotten`, userFiscalCode)
      .then((response) => {
        setPopUpText('Email di reset inviata correttamente');
        showPopup(true);
        setTimeout(() => {
          handleCloseResetPassword();
        }, POPUP_DURATION_DELAY);
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  /* ------------------------------- Delete User ------------------------------ */

  const [openDeleteUserDialog, setOpenDeleteUserDialog] = useState(false);

  const handleOpenDeleteUserDialog = () => {
    setOpenDeleteUserDialog(true);
  };

  const handleCloseDeleteUserDialog = () => {
    setOpenDeleteUserDialog(false);
  };

  const handleDeleteUser = () => {
    API.delete(`/sanitize-user?id=${userID}`)
      .then((response) => {
        console.log(response);
        setPopUpText('Utente eliminato correttamente');
        showPopup(true);
        handleCloseDeleteUserDialog();
        navigate('/admin/users');
      })
      .catch((error) => {
        HttpErrorHandler(error, setPopUpText);
        showPopup(false);
      });
  };

  return (
    <>
      {isLoading ? (
        LoadingUI()
      ) : errorMessage.length === 0 ? (
        <Grid container spacing={3} alignItems="flex-start !important">
          <Grid item xs={12} md={4}>
            <Grid container spacing={3}>
              {/* User Card  */}
              <Grid item xs={12}>
                <Card sx={{ p: '24px' }}>
                  <List>
                    <ListItem sx={{ d: 'flex', justifyContent: 'center' }}>
                      <Avatar sx={{ width: '150px', height: '150px' }} src="/assets/images/img/user.png" />
                    </ListItem>

                    <ListItem sx={{ d: 'flex', justifyContent: 'center', pb: '20px' }}>
                      {userInfo.name_alias === null
                        ? `${userInfo.name || ''} ${userInfo.surname || ''}`
                        : userInfo.name_alias}
                    </ListItem>

                    <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Stack direction="column" alignItems="center" justifyContent="center" spacing={1}>
                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                          <Typography
                            sx={{
                              padding: '4px 10px',
                              fontSize: '.8em',
                              backgroundColor: userInfo.verified ? '#dbf6e5' : '#ffe4de',
                              color: userInfo.verified ? '#118d57' : '#b71d18',
                              borderRadius: '16px',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold',
                              textAlign: 'center',
                            }}
                          >
                            {userInfo.verified ? 'Verificato' : 'Non Verificato'}
                          </Typography>

                          <Typography
                            sx={{
                              padding: '4px 10px',
                              fontSize: '.8em',
                              backgroundColor: userInfo.current_campcard_payed ? '#dbf6e5' : '#ffe4de',
                              color: userInfo.current_campcard_payed ? '#118d57' : '#b71d18',
                              borderRadius: '16px',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold',
                              textAlign: 'center',
                            }}
                          >
                            {userInfo.current_campcard_payed ? 'Tesserato' : 'Non Tesserato'}
                          </Typography>
                        </Stack>

                        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
                          <Typography
                            sx={{
                              padding: '4px 10px',
                              fontSize: '.8em',
                              backgroundColor: userInfo.user_online ? '#dbf6e5' : '#ffe4de',
                              color: userInfo.user_online ? '#118d57' : '#b71d18',
                              borderRadius: '16px',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold',
                              textAlign: 'center',
                            }}
                          >
                            {userInfo.user_online ? 'Online' : 'Offline'}
                          </Typography>
                          <Typography
                            sx={{
                              padding: '4px 10px',
                              fontSize: '.8em',
                              backgroundColor: !userInfo.blocked ? '#dbf6e5' : '#ffe4de',
                              color: !userInfo.blocked ? '#118d57' : '#b71d18',
                              borderRadius: '16px',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold',
                              textAlign: 'center',
                            }}
                          >
                            {!userInfo.blocked ? 'Attivo' : 'Bloccato'}
                          </Typography>
                        </Stack>
                      </Stack>
                    </ListItem>
                  </List>
                </Card>
              </Grid>

              {/* manage user statuses card */}
              <Grid item xs={12}>
                <Card sx={{ padding: '24px' }}>
                  <CardHeader sx={{ padding: '0' }} title="Gestione utente" />
                  <Stack direction="column" justifyContent="center" alignItems="start">
                    {/* Verify user */}
                    <FormControlLabel
                      sx={{ paddingTop: '12px' }}
                      control={
                        <Switch
                          disabled={!canModifyUser}
                          checked={Boolean(verifiedUser)}
                          onClick={onOpenVerifyUserDialog}
                        />
                      }
                      label={verifiedUser ? 'Utente verificato' : 'Utente non verificato'}
                    />

                    {/* Verify User Dialog */}
                    <DecisionDialog
                      openDialog={openVerifyUserDialog}
                      dialogTitle={`${verifiedUser ? 'Verifica' : 'Rimuovi verifica'} utente`}
                      dialogDescription={`Sei sicuro di voler ${
                        verifiedUser ? "rimuovere la verifica all'" : "verificare l'"
                      } utente?`}
                      closeOperation={onCloseVerifyUserDialog}
                      requestOperation={changeVerifiedUserValue}
                      requestText={verifiedUser ? 'Rimuovi verifica' : 'Verifica'}
                      closeText={'Chiudi'}
                      RequestButton={DefaultContainedButton}
                    />

                    {/* Online user */}
                    <FormControlLabel
                      sx={{ paddingTop: '12px' }}
                      control={
                        <Switch
                          disabled={!canModifyUser}
                          checked={Boolean(onlineUser)}
                          onClick={onOpenOnlineUserDialog}
                        />
                      }
                      label={onlineUser ? 'Utente online' : 'Utente offline'}
                    />

                    {/* Online User Dialog */}
                    <DecisionDialog
                      openDialog={openOnlineUserDialog}
                      dialogTitle={`${onlineUser ? 'Utente Online' : 'Utente Offline'}`}
                      dialogDescription={`Sei sicuro di voler ${
                        onlineUser
                          ? 'impostare come offline il seguente utente?'
                          : 'impostare come online il seguente utente?'
                      }`}
                      closeOperation={onCloseOnlineUserDialog}
                      requestOperation={changeOnlineUserValue}
                      requestText={onlineUser ? 'Imposta Offline' : 'Imposta Online'}
                      closeText={'Chiudi'}
                      RequestButton={DefaultContainedButton}
                    />

                    {/* Lock User */}
                    <FormControlLabel
                      sx={{ paddingTop: '12px', alignItems: 'center' }}
                      control={
                        <Switch
                          disabled={!canModifyUser}
                          checked={Boolean(!lockedUser)}
                          onClick={onOpenLockedUserDialog}
                        />
                      }
                      label={
                        <Box>
                          <Typography>{!lockedUser ? 'Utente attivo' : 'Utente bloccato'}</Typography>

                          {Boolean(lockedUser) && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5, wordBreak: 'break-word' }}
                            >
                              Motivo: {userInfo?.block_reason?.name}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    {/* Lock User Dialog */}
                    <Dialog fullWidth open={openLockedUserDialog} onClose={() => {}}>
                      <DialogTitle>{`${!lockedUser ? 'Blocca' : 'Sblocca'} utente`}</DialogTitle>
                      <DialogContent>
                        <Typography sx={{ pb: 2 }}>
                          {!lockedUser
                            ? `Per continuare è necessario selezionare una motivazione.`
                            : `Sei sicuro di voler sbloccare l'utente?`}
                        </Typography>

                        {!lockedUser && (
                          <CustomAutocomplete
                            options={blockReasonsList}
                            variant="outlined"
                            renderInput={(params) => <TextField {...params} label="Motivazione *" />}
                            getOptionLabel={(option) => option.name}
                            onChange={(e, value) => setSelectedBlockReason(value)}
                          />
                        )}
                      </DialogContent>
                      <DialogActions>
                        <DefaultContainedButton onClick={!lockedUser ? handleLockUser : handleUnlockUser}>
                          {!lockedUser ? 'Blocca' : 'Sblocca'}
                        </DefaultContainedButton>
                        <DefaultOutlinedButton onClick={onCloseLockedUserDialog}>Chiudi</DefaultOutlinedButton>
                      </DialogActions>
                    </Dialog>

                    {/* Allow Newsletter */}
                    <FormControlLabel
                      sx={{ paddingTop: '12px' }}
                      control={
                        <Switch disabled={!canModifyUser} checked={allowNews} onClick={onOpenNewsletterDialog} />
                      }
                      label="Riceve comunicazioni"
                    />

                    {/* Allow Newsletter Dialog */}
                    <DecisionDialog
                      openDialog={openNewsletterDialog}
                      dialogTitle={`${allowNews ? 'Disattiva' : 'Attiva'} comunicazioni`}
                      dialogDescription={`Sei sicuro di voler ${
                        allowNews ? 'disattivare' : 'attivare'
                      } le comunicazioni?`}
                      closeOperation={onCloseNewsletterDialog}
                      requestOperation={changeNewsletterPermission}
                      requestText={allowNews ? 'Disattiva' : 'Attiva'}
                      closeText={'Chiudi'}
                      RequestButton={DefaultContainedButton}
                    />
                  </Stack>
                  {canDeleteUsers && (
                    <Box sx={{ paddingTop: '12px' }}>
                      <RedOutlinedButton onClick={() => handleOpenDeleteUserDialog()}>Elimina Utente</RedOutlinedButton>
                    </Box>
                  )}
                </Card>

                <DecisionDialog
                  openDialog={openDeleteUserDialog}
                  dialogTitle={'Elimina utente'}
                  dialogDescription={`Sei sicuro di voler eliminare questo utente?  L'operazione è irreversibile e verranno eliminati tutti i dati ad esso collegati.`}
                  closeOperation={handleCloseDeleteUserDialog}
                  requestOperation={handleDeleteUser}
                  requestText={'Elimina'}
                  closeText={'Chiudi'}
                  RequestButton={RedOutlinedButton}
                />
              </Grid>

              {/* Privacy Policy  */}
              <Grid item xs={12}>
                <Card
                  sx={{
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'start',
                    gap: '16px',
                  }}
                >
                  <CardHeader sx={{ padding: '0' }} title="Informazioni Utente" />

                  <Box sx={{ marginTop: '12px' }}>
                    <Typography>
                      <Typography variant="span" sx={{ fontWeight: 'bold' }}>
                        Privacy policy:{' '}
                      </Typography>
                      {userInfo.privacy_check
                        ? `Accettata il ${dayjs(userInfo.privacy_check_date).format('DD/MM/YYYY')}`
                        : `Non accettata`}
                    </Typography>

                    <Typography sx={{ marginTop: '12px' }}>
                      <Typography variant="span" sx={{ fontWeight: 'bold' }}>
                        Origine utente:{' '}
                      </Typography>
                      {userInfo.user_origin}
                    </Typography>

                    <Typography sx={{ marginTop: '12px' }}>
                      <Typography variant="span" sx={{ fontWeight: 'bold' }}>
                        Registrato da:{' '}
                      </Typography>
                      {userInfo.id === userInfo.created_user
                        ? 'Registrazione Autonoma'
                        : `${userInfo.created_user_name}`}
                    </Typography>

                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      gap={1}
                      sx={{ marginTop: '12px' }}
                    >
                      <Typography variant="span" sx={{ fontWeight: 'bold' }}>
                        Fase:{' '}
                      </Typography>
                      <CommonLabel
                        textColor="white"
                        bgColor={
                          userInfo.pipeline_stage === 'contact'
                            ? '#1a73e8'
                            : userInfo.pipeline_stage === 'member'
                            ? 'green'
                            : 'red'
                        }
                        text={
                          userInfo.pipeline_stage === 'contact'
                            ? 'Contatto'
                            : userInfo.pipeline_stage === 'member'
                            ? 'Membro'
                            : 'Utente'
                        }
                      />
                    </Box>
                  </Box>
                </Card>
              </Grid>

              {/* Reset password card */}
              <Grid item xs={12}>
                <Card
                  sx={{
                    padding: '24px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    gap: '16px',
                  }}
                >
                  <CardHeader sx={{ padding: '0' }} title="Gestione Password" />
                  <Box sx={{ marginTop: '12px' }}>
                    <DefaultContainedButton disabled={!canModifyUser} onClick={handleOpenResetPassword}>
                      Reset Password
                    </DefaultContainedButton>

                    <Dialog fullWidth open={openResetPassword} onClose={() => {}}>
                      <DialogTitle sx={{ textAlign: 'center' }}>Reset Password</DialogTitle>

                      <form onSubmit={handleResetPassword}>
                        <DialogContent>
                          <Grid container spacing={3}>
                            <Grid item xs={12} sx={{ overflow: 'auto' }}>
                              <Box>
                                Cliccando su 'Continua' verrà inviata tramite mail una password temporanea
                                all'indirizzo:{' '}
                                <Typography sx={{ display: 'inline', fontWeight: 'bold' }}>{userInfo.email}</Typography>{' '}
                                . Seguendo le indicazioni della mail sarà possibile reimpostare la password.
                              </Box>
                            </Grid>
                          </Grid>
                        </DialogContent>
                        <DialogActions sx={{ paddingRight: 3, paddingBottom: 3 }}>
                          <DefaultOutlinedButton onClick={handleCloseResetPassword}>Chiudi</DefaultOutlinedButton>
                          <DefaultContainedButton type="submit" customStyles={{ height: '34px' }}>
                            Continua
                          </DefaultContainedButton>
                        </DialogActions>
                      </form>
                    </Dialog>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* User data card */}
          <Grid item xs={12} md={8}>
            <Card sx={{ p: '24px' }}>
              <form onSubmit={updateUserData}>
                {!isEditApisRunning ? (
                  <Grid container spacing={3} sx={{ justifyContent: 'start !important' }}>
                    {/* Peronal Title */}
                    <Grid item xs={12}>
                      <Typography variant="h6">Informazioni Personali</Typography>
                    </Grid>

                    {/* Name */}
                    <Grid item xs={12} md={6}>
                      <CustomTextField
                        error={errors.name}
                        disabled={!edit || (!isContactUser && !canManageUser)}
                        onChange={(e) => {
                          setName(e.currentTarget.value);
                        }}
                        value={name}
                        label="Nome"
                      />
                    </Grid>

                    {/* Surname */}
                    <Grid item xs={12} md={6}>
                      <CustomTextField
                        disabled={!edit || (!isContactUser && !canManageUser)}
                        error={errors.surname}
                        onChange={(e) => {
                          setSurname(e.currentTarget.value);
                        }}
                        value={surname}
                        label="Cognome"
                      />
                    </Grid>

                    {/* Alias */}
                    <Grid item xs={12} md={useAlias ? 6 : 12} sx={{ height: '80px' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            disabled={!edit}
                            checked={useAlias}
                            onChange={(e) => {
                              setUseAlias(e.target.checked);
                            }}
                          />
                        }
                        label="Utilizza nome alias"
                      />
                    </Grid>
                    {useAlias && (
                      <>
                        {/* name alias */}
                        <Grid item xs={12} md={6}>
                          <CustomTextField
                            error={errors.name_alias}
                            disabled={!edit}
                            onChange={(e) => {
                              setAliasName(e.currentTarget.value);
                            }}
                            value={aliasName}
                            label="Alias"
                          />
                        </Grid>
                      </>
                    )}

                    {/* gender */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        options={GenderList}
                        disabled={!edit}
                        getOptionLabel={(option) => option.gender}
                        isOptionEqualToValue={(option, value) => option.gender === value.gender}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.gender}>
                            {option.gender}
                          </Box>
                        )}
                        onChange={(e, value) => {
                          setGender(value);
                        }}
                        defaultValue={gender}
                        renderInput={(params) => (
                          <TextField {...params} label="Sesso" error={errors.gender} variant="outlined" />
                        )}
                      />
                    </Grid>

                    {/* Birth Date */}
                    <Grid item xs={12} md={6}>
                      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <CustomDatePicker
                          error={errors.birth_date}
                          disabled={!edit || (!isContactUser && !canManageUser)}
                          label="Data di Nascita *"
                          value={dayjs(birthDate)}
                          onChange={handleBirthDateChange}
                        />
                      </LocalizationProvider>
                    </Grid>

                    {/* Birth Place */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        options={birthPlacesList}
                        disabled={!edit || (!isContactUser && !canManageUser)}
                        getOptionLabel={(option) => option.name}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name}
                          </Box>
                        )}
                        defaultValue={birthPlace}
                        onChange={(e, value) => {
                          setBirthPlace(value);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Luogo di Nascita"
                            error={errors.place_of_birth}
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>

                    {/* Fiscal Code */}
                    <Grid item xs={12} md={6}>
                      <CustomTextField
                        error={errors.fiscal_code}
                        disabled={!edit || (!isContactUser && !canManageUser)}
                        defaultValue={fiscalCode}
                        onChange={(e) => {
                          setFiscalCode(e.currentTarget.value);
                        }}
                        label="Codice Fiscale"
                      />
                    </Grid>

                    {/* Entity */}
                    <Grid item xs={12} md={6} sx={{ alignSelf: 'start' }}>
                      <CustomTextField
                        error={errors.entity_id}
                        disabled={!edit}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment
                              position="end"
                              sx={{ cursor: 'pointer', display: canModifyUser ? 'flex' : 'none' }}
                            >
                              <Tooltip title="Modifica" placement="top" arrow>
                                <EditIcon
                                  onClick={() => {
                                    if (edit) {
                                      setOpenEntitiesFilter(!openEntitiesFilter);
                                    }
                                  }}
                                />
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        value={userEntity ? userEntity.name : ''}
                        label="Circolo"
                      />

                      <EntitiesDialog
                        openDialog={openEntitiesFilter}
                        closeDialog={handleCloseEntitiesFilterDialog}
                        outputEntity={getSelectedEntity}
                        context={'userDetails'}
                      />
                    </Grid>

                    {/* Entità master GD — solo se l'utente è abilitato ai GD (età 16–29) */}
                    {birthDate && isGdEligibleAge(dayjs().diff(dayjs(birthDate), 'year')) && (
                      <Grid item xs={12} md={6} sx={{ alignSelf: 'start' }}>
                        <GdMasterEntitySelect
                          value={userGdEntity}
                          onChange={setUserGdEntity}
                          options={gdEntityList}
                          disabled={!edit}
                          label="Entità GD"
                        />
                      </Grid>
                    )}

                    {/* Email */}
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <CustomTextField
                        error={errors.email}
                        disabled={!canModifyUser || !edit}
                        value={email}
                        onChange={(e) => {
                          setEmail(e.currentTarget.value);
                        }}
                        label="Email"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {userInfo.verified_email ? (
                                <Tooltip arrow title="Email verificata" placement="top">
                                  <VerifiedIcon sx={{ color: 'green !important' }} />
                                </Tooltip>
                              ) : (
                                <Tooltip arrow title="Email non verificata" placement="top">
                                  <WarningAmberIcon sx={{ color: 'orange !important' }} />
                                </Tooltip>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />

                      {!userInfo.verified_email && (
                        <CustomIconButton sx={{ width: '40px', height: '40px' }} onClick={handleClickEmailMenu}>
                          <MoreVertIcon />
                        </CustomIconButton>
                      )}

                      <Menu anchorEl={emailMenuAnchor} open={openEmailMenu} onClose={handleCloseEmailMenu}>
                        <MenuItem
                          onClick={() => {
                            handleOpenVerifyEmailDialog();
                          }}
                        >
                          Verifica con OTP
                        </MenuItem>

                        {canManageUser && (
                          <MenuItem
                            onClick={() => {
                              verifyUserEmail();
                            }}
                          >
                            Verifica Forzata
                          </MenuItem>
                        )}
                      </Menu>
                      {/* Email verification dialog */}
                      <Dialog fullWidth open={openVerifyEmailDialog} onClose={() => {}}>
                        <DialogTitle>Verifica E-mail</DialogTitle>

                        <DialogContent>
                          <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                            {/* Email Address */}
                            <Stack
                              direction={is600Screen ? 'row' : 'column'}
                              justifyContent="center"
                              alignItems="center"
                              gap="6px"
                            >
                              <Typography variant="h6">E-mail:</Typography>
                              <Typography variant="span">{email}</Typography>
                              {isUserVerifyingEmail && !verifiedEmail && (
                                <Tooltip title="In attesa di verifica" placement="right">
                                  <AutorenewIcon sx={{ color: '#414670' }} />
                                </Tooltip>
                              )}
                              {verifiedEmail && (
                                <Tooltip title="Email verificata" placement="right">
                                  <CheckIcon sx={{ color: 'green' }} />
                                </Tooltip>
                              )}
                            </Stack>
                            {!verifiedEmail && isUserVerifyingEmail && (
                              <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                {/* Input & Submit Button */}
                                <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                  <CustomTextField
                                    size="small"
                                    inputProps={{ maxLength: 6 }}
                                    type="tel"
                                    label="Inserisci il codice"
                                  />
                                  <DefaultOutlinedButton onClick={() => verifyUserEmail()}>
                                    Verifica
                                  </DefaultOutlinedButton>
                                </Stack>

                                {/* Resend Email verifcation code */}
                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography>Non hai ricevuto l'email?</Typography>
                                  <Typography
                                    sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
                                    onClick={() => sendOtpEmail()}
                                  >
                                    Inviala di nuovo
                                  </Typography>
                                </Box>
                              </Stack>
                            )}

                            {!verifiedEmail && !isUserVerifyingEmail && (
                              <DefaultOutlinedButton onClick={() => sendOtpEmail()}>
                                Verifica Email
                              </DefaultOutlinedButton>
                            )}
                          </Stack>
                        </DialogContent>

                        <DialogActions>
                          <DefaultOutlinedButton onClick={handleCloseVerifyEmailDialog}>Chiudi</DefaultOutlinedButton>
                        </DialogActions>
                      </Dialog>
                    </Grid>

                    {/* Prefix */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        disabled={!edit || (!isContactUser && !canModifyUser)}
                        options={resCountriesList}
                        value={
                          resCountriesList.length > 0
                            ? resCountriesList.find((option) => option.call_code === callCode) || null
                            : { call_code: callCode }
                        }
                        getOptionLabel={(option) => (typeof option === 'string' ? option : option.call_code || '')}
                        isOptionEqualToValue={(option, value) =>
                          typeof value === 'string' ? option.call_code === value : option.call_code === value.call_code
                        }
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name} {option.call_code}
                          </Box>
                        )}
                        filterOptions={(options, state) => {
                          const newOptions = [];
                          options.forEach((element) => {
                            if (
                              element.name.toLowerCase().includes(state.inputValue.toLowerCase()) ||
                              element.call_code.toLowerCase().includes(state.inputValue.toLowerCase())
                            ) {
                              newOptions.push(element);
                            }
                          });
                          return newOptions;
                        }}
                        onChange={(e, value) => {
                          setCallCode(value?.call_code || value || '');
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Prefisso *" error={errors.call_code} variant="outlined" />
                        )}
                      />
                    </Grid>

                    {/* Phone */}
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                    >
                      <CustomTextField
                        disabled={!edit || (!isContactUser && !canModifyUser)}
                        error={errors.phone}
                        value={phoneNumber}
                        onChange={(event) => validatePhoneNumber(event, setPhoneNumber)}
                        type="tel"
                        label="Cellulare"
                        onKeyDown={(e) => {
                          if (e.ctrlKey || e.metaKey) {
                            if (['c', 'v', 'x', 'a', 'z'].includes(e.key.toLowerCase())) {
                              return;
                            }
                          }

                          if (
                            e.key !== 'Backspace' &&
                            !/[0-9]/.test(e.key) &&
                            !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Delete'].includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          const paste = e.clipboardData.getData('text');
                          if (!/^\d+$/.test(paste)) {
                            e.preventDefault();
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {userInfo.verified_phone ? (
                                <Tooltip arrow title="Numero verificato" placement="top">
                                  <VerifiedIcon sx={{ color: 'green !important' }} />
                                </Tooltip>
                              ) : (
                                <Tooltip arrow title="Numero non verificato" placement="top">
                                  <WarningAmberIcon sx={{ color: 'orange !important' }} />
                                </Tooltip>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />

                      {!userInfo.verified_phone && (
                        <CustomIconButton sx={{ width: '40px', height: '40px' }} onClick={handleClickPhoneMenu}>
                          <MoreVertIcon />
                        </CustomIconButton>
                      )}

                      <Menu anchorEl={phoneMenuAnchor} open={openPhoneMenu} onClose={handleClosePhoneMenu}>
                        <MenuItem
                          onClick={() => {
                            handleOpenVerifyPhoneDialog();
                          }}
                        >
                          Verifica con OTP
                        </MenuItem>

                        {canManageUser && (
                          <MenuItem
                            onClick={() => {
                              verifyUserPhone();
                            }}
                          >
                            Verifica Forzata
                          </MenuItem>
                        )}
                      </Menu>

                      {/* Phone number verification dialog */}
                      <Dialog fullWidth open={openVerifyPhoneDialog} onClose={() => {}}>
                        <DialogTitle>Verifica Numero</DialogTitle>

                        <DialogContent>
                          {/* Phone Verification */}
                          <Stack
                            justifyContent="center"
                            alignItems="center"
                            gap="12px"
                            id="register-email-token-container"
                          >
                            <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                              {/* Phone Number */}
                              <Stack
                                direction={is600Screen ? 'row' : 'column'}
                                justifyContent="center"
                                alignItems="center"
                                gap="6px"
                              >
                                <Typography variant="h6">Numero di telefono:</Typography>
                                <Typography variant="span">
                                  {callCode}
                                  {phoneNumber}
                                </Typography>
                                {isUserVerifyingPhone && !verifiedPhone && (
                                  <Tooltip title="In attesa di verifica" placement="right">
                                    <AutorenewIcon sx={{ color: '#414670' }} />
                                  </Tooltip>
                                )}

                                {verifiedPhone && (
                                  <Tooltip title="Numero verificato" placement="right">
                                    <CheckIcon sx={{ color: 'green' }} />
                                  </Tooltip>
                                )}
                              </Stack>

                              {isUserVerifyingPhone && !verifiedPhone && (
                                <Stack direction="column" justifyContent="center" alignItems="center" gap="12px">
                                  {/* Input & Submit Button */}
                                  <Stack direction="row" justifyContent="center" alignItems="center" gap="12px">
                                    <CustomTextField
                                      size="small"
                                      inputProps={{ maxLength: 6 }}
                                      label="Inserisci il codice"
                                    />
                                    <DefaultOutlinedButton disabled={verifiedPhone} onClick={() => verifyUserPhone()}>
                                      Verifica
                                    </DefaultOutlinedButton>
                                  </Stack>

                                  {/* Resend SMS */}
                                  <Box sx={{ textAlign: 'center' }}>
                                    <Typography>Non hai ricevuto l'sms?</Typography>
                                    <Typography
                                      sx={{ textDecoration: 'underline', color: 'gray', cursor: 'pointer' }}
                                      onClick={() => sendOtpSms()}
                                    >
                                      Invialo di nuovo
                                    </Typography>
                                  </Box>
                                </Stack>
                              )}
                              {!verifiedPhone && !isUserVerifyingPhone && (
                                <DefaultOutlinedButton onClick={() => sendOtpSms()}>
                                  Verifica Numero
                                </DefaultOutlinedButton>
                              )}
                            </Stack>
                          </Stack>
                        </DialogContent>
                        <DialogActions>
                          <DefaultOutlinedButton onClick={handleCloseVerifyPhoneDialog}>Chiudi</DefaultOutlinedButton>
                        </DialogActions>
                      </Dialog>
                    </Grid>

                    {/* Role */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        multiple
                        disabled={!canManageUserRoles || !edit}
                        options={rolesList.filter((role) => role.prof_type_name !== 'BACKOFFICE')}
                        value={userRoles}
                        isOptionEqualToValue={(option, value) => option.prof_type_id === value.prof_type_id}
                        getOptionLabel={(option) => option.prof_type_name}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.prof_type_id}>
                            {option.prof_type_name}
                          </Box>
                        )}
                        onChange={(e, value) => {
                          setUserRoles(value);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Ruolo" error={errors.user_roles} variant="outlined" />
                        )}
                      />
                    </Grid>

                    {/* Res Title */}
                    <Grid item xs={12}>
                      <Typography variant="h6">Informazioni di Residenza</Typography>
                    </Grid>

                    {/* Res Country */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        required
                        id="res-country"
                        disabled={!edit}
                        options={
                          resCountry?.name === italy.name
                            ? resCountriesList.filter(
                                (country) =>
                                  (!resRegion || country.id === resRegion.country_id) &&
                                  (!resProvince || country.id === resProvince.country_id)
                              )
                            : resCountriesList
                        }
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.name}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name}
                          </Box>
                        )}
                        value={resCountry}
                        onChange={(e, value) => {
                          setResCountry(value);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Paese *" error={errors.res_country_id} variant="outlined" />
                        )}
                      />
                    </Grid>

                    {/* Res Region */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        id="res-region"
                        required
                        freeSolo={resCountry?.name !== italy?.name}
                        disabled={!edit}
                        options={resRegionList.filter(
                          (region) =>
                            (!resCountry || region.country_id === resCountry.id) &&
                            (!resProvince || region.region_name === resProvince.region_name)
                        )}
                        isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option;
                          }
                          if (option.inputValue) {
                            return option.inputValue;
                          }
                          return option.region_name;
                        }}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.region_name}>
                            {option.region_name}
                          </Box>
                        )}
                        value={
                          resRegionName === undefined || resRegionName === null || resCountry?.name === italy?.name
                            ? resRegion
                            : resRegionName
                        }
                        onChange={(e, value) => {
                          if (typeof value === 'string') {
                            setResRegion(value);
                          } else if (value && value.inputValue) {
                            setResRegion(value.inputValue);
                          } else {
                            setResRegion(value);
                          }
                        }}
                        onInputChange={(e, value) => {
                          if (resCountry?.name !== italy?.name) {
                            setResRegionName(value);
                          } else {
                            setResRegionName(null);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField {...params} label="Regione *" error={errors.res_region_name} variant="outlined" />
                        )}
                      />
                    </Grid>

                    {/* Res Province */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        id="res-province"
                        required
                        freeSolo={resCountry?.name !== italy?.name}
                        disabled={!edit || (resCountry && resCountry?.name !== italy.name)}
                        options={resProvincesList.filter(
                          (province) =>
                            (!resRegion || province.region_name === resRegion.region_name) &&
                            (!resCity || province.id === resCity.province_id) &&
                            (!resCountry || province.country_id === resCountry.id)
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option;
                          }
                          if (option.inputValue) {
                            return option.inputValue;
                          }
                          return option.name;
                        }}
                        value={resProvince}
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name}
                          </Box>
                        )}
                        onChange={(e, value) => {
                          if (typeof value === 'string') {
                            setResProvince(value);
                          } else if (value && value.inputValue) {
                            setResProvince(value.inputValue);
                          } else {
                            setResProvince(value);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={`Provincia ${resCountry?.name === italy.name ? '*' : ''}`}
                            variant="outlined"
                            error={errors.res_province_id}
                          />
                        )}
                      />
                    </Grid>

                    {/* Res City */}
                    <Grid item xs={12} md={6}>
                      <CustomAutocomplete
                        id="res-city"
                        required
                        freeSolo={resCountry?.name !== italy?.name}
                        disabled={!edit}
                        options={resCitiesList.filter((city) => !resProvince || city.province_id === resProvince.id)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') {
                            return option;
                          }
                          if (option.inputValue) {
                            return option.inputValue;
                          }
                          return option.name;
                        }}
                        value={
                          resCityName === undefined || resCityName === null || resCountry?.name === italy?.name
                            ? resCity
                            : resCityName
                        }
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name}
                          </Box>
                        )}
                        onChange={(e, value) => {
                          if (typeof value === 'string') {
                            setResCity(value);
                          } else if (value && value.inputValue) {
                            setResCity(value.inputValue);
                          } else {
                            setResCity(value);
                          }
                        }}
                        onInputChange={(e, value) => {
                          if (resCountry?.name !== italy?.name) {
                            setResCityName(value);
                          } else {
                            setResCityName(null);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Comune *"
                            variant="outlined"
                            error={errors.res_city_id || errors.res_city_name}
                          />
                        )}
                      />
                    </Grid>

                    {/* Res Street */}
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        id="res-street"
                        error={errors.res_street}
                        disabled={!edit}
                        fullWidth
                        name="resStreet"
                        label="Indirizzo di Residenza"
                        value={resStreet}
                        onChange={(e) => {
                          setResStreet(e.target.value);
                        }}
                      />
                    </Grid>

                    {/* Res Zip Code */}
                    <Grid item xs={12} sm={6}>
                      <CustomTextField
                        id="res-zipcode"
                        error={errors.res_zip_code}
                        disabled={!edit}
                        fullWidth
                        label="CAP di Residenza"
                        name="resZipCode"
                        onChange={(e) => {
                          setResZipCode(e.target.value);
                        }}
                        value={resZipCode}
                      />
                    </Grid>

                    {/* Domicile */}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch color="primary" checked={domIsRes} onChange={handleSwitchChange} disabled={!edit} />
                        }
                        label="Le informazioni di Domicilio corrispondono a quelle di Residenza"
                      />
                    </Grid>

                    {/* Domicile info container */}
                    {!domIsRes && (
                      <Grid container spacing={3} sx={{ margin: '0' }}>
                        {/* Dom Title */}
                        <Grid item xs={12}>
                          <Typography variant="h6">Informazioni di Domicilio</Typography>
                        </Grid>

                        {/* Dom Country */}
                        <Grid item xs={12} md={6}>
                          <CustomAutocomplete
                            id="dom-country"
                            disabled={!edit}
                            options={
                              domCountry?.name === italy.name
                                ? domCountriesList.filter(
                                    (country) =>
                                      (!domRegion || country.id === domRegion.country_id) &&
                                      (!domProvince || country.id === domProvince.country_id)
                                  )
                                : domCountriesList
                            }
                            getOptionLabel={(option) => option.name}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                {option.name}
                              </Box>
                            )}
                            value={domCountry}
                            onChange={(e, value) => {
                              setDomCountry(value);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Paese *"
                                error={errors.home_country_id}
                                variant="outlined"
                              />
                            )}
                          />
                        </Grid>

                        {/* Dom Region */}
                        <Grid item xs={12} md={6}>
                          <CustomAutocomplete
                            id="dom-region"
                            freeSolo={domCountry?.name !== italy?.name}
                            disabled={!edit}
                            options={domRegionList.filter(
                              (region) =>
                                (!domCountry || region.country_id === domCountry.id) &&
                                (!domProvince || region.region_name === domProvince.region_name)
                            )}
                            isOptionEqualToValue={(option, value) => option.region_name === value.region_name}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') {
                                return option;
                              }
                              if (option.inputValue) {
                                return option.inputValue;
                              }
                              return option.region_name || '';
                            }}
                            value={
                              domRegionName !== null && domRegionName !== undefined && domCountry?.name !== italy?.name
                                ? domRegionName
                                : domRegion
                            }
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.region_name}>
                                {option.region_name}
                              </Box>
                            )}
                            onChange={(e, value) => {
                              if (typeof value === 'string') {
                                setDomRegion(value);
                              } else if (value && value.inputValue) {
                                setDomRegion(value.inputValue);
                              } else {
                                setDomRegion(value);
                              }
                            }}
                            onInputChange={(e, value) => {
                              if (domCountry?.name !== italy?.name) {
                                setDomRegionName(value);
                              } else {
                                setDomRegionName(null);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={errors.home_region_name}
                                label="Regione *"
                                variant="outlined"
                              />
                            )}
                          />
                        </Grid>

                        {/* Dom Province */}
                        <Grid item xs={12} md={6}>
                          <CustomAutocomplete
                            id="dom-province"
                            freeSolo={domCountry?.name !== italy?.name}
                            disabled={!edit || (domCountry && domCountry?.name !== italy.name)}
                            options={domProvincesList.filter(
                              (province) =>
                                (!domRegion || province.region_name === domRegion.region_name) &&
                                (!domCity || province.id === domCity.province_id)
                            )}
                            isOptionEqualToValue={(option, value) => value.id === option.id}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') {
                                return option;
                              }
                              if (option.inputValue) {
                                return option.inputValue;
                              }
                              return option.name || '';
                            }}
                            value={domProvince}
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                {option.name}
                              </Box>
                            )}
                            onChange={(e, value) => {
                              if (typeof value === 'string') {
                                setDomProvince(value);
                              } else if (value && value.inputValue) {
                                setDomProvince(value.inputValue);
                              } else {
                                setDomProvince(value);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={`Provincia ${domCountry?.name === italy.name ? '*' : ''}`}
                                variant="outlined"
                                error={errors.home_province_id}
                              />
                            )}
                          />
                        </Grid>

                        {/* Dom City */}
                        <Grid item xs={12} md={6}>
                          <CustomAutocomplete
                            id="dom-city"
                            freeSolo={domCountry?.name !== italy?.name}
                            disabled={!edit}
                            options={domCitiesList.filter(
                              (city) => !domProvince || city.province_id === domProvince.id
                            )}
                            isOptionEqualToValue={(option, value) => value.id === option.id}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') {
                                return option;
                              }
                              if (option.inputValue) {
                                return option.inputValue;
                              }
                              return option.name || '';
                            }}
                            value={
                              domCityName !== null && domCityName !== undefined && domCountry?.name !== italy?.name
                                ? domCityName
                                : domCity
                            }
                            renderOption={(props, option) => (
                              <Box component="li" {...props} key={option.id}>
                                {option.name}
                              </Box>
                            )}
                            onChange={(e, value) => {
                              if (typeof value === 'string') {
                                setDomCity(value);
                              } else if (value && value.inputValue) {
                                setDomCity(value.inputValue);
                              } else {
                                setDomCity(value);
                              }
                            }}
                            onInputChange={(e, value) => {
                              if (domCountry?.name !== italy?.name) {
                                setDomCityName(value);
                              } else {
                                setDomCityName(null);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Comune *"
                                variant="outlined"
                                error={errors.home_city_id || errors.home_city_name}
                              />
                            )}
                          />
                        </Grid>

                        {/* Dom Street */}
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            id="dom-street"
                            disabled={!edit}
                            fullWidth
                            label="Indirizzo di Domicilio *"
                            onChange={(e) => setDomStreet(e.target.value)}
                            value={domStreet}
                            error={errors.home_street}
                          />
                        </Grid>

                        {/* Dom Zip Code */}
                        <Grid item xs={12} sm={6}>
                          <CustomTextField
                            id="dom-zipcode"
                            disabled={!edit}
                            fullWidth
                            label="CAP di Domicilio *"
                            onChange={(e) => {
                              setDomZipCode(e.currentTarget.value);
                            }}
                            value={domZipCode}
                            error={errors.home_zip_code}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {/* Submit / close buttons */}
                    <Grid item xs={12} sx={{ display: canModifyUser ? 'block' : 'none' }}>
                      <Stack direction="row" justifyContent="end" gap="12px">
                        {edit && (
                          <DefaultOutlinedButton onClick={handleCloseEditButtonClick}>Annulla</DefaultOutlinedButton>
                        )}
                        <DefaultContainedButton type="submit">{edit ? 'Salva' : 'Modifica'}</DefaultContainedButton>
                      </Stack>
                    </Grid>
                  </Grid>
                ) : (
                  // Loading
                  <Stack
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      minHeight: domIsRes ? '764px' : '1052px',
                    }}
                  >
                    <CircularProgress />
                  </Stack>
                )}
              </form>
            </Card>
          </Grid>
        </Grid>
      ) : (
        // Error handler
        ErrorUI(errorMessage)
      )}
    </>
  );
}
